import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { CheckCircle2, X, RotateCcw, RefreshCw, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared Q&A console used by:
 *   - Admin → Q&A tab (no token; uses admin auth)
 *   - /qa/chair?token=... public chair page (no admin auth)
 *
 * Props:
 *   - token (optional) — appended as ?token=... on every request
 *   - title / subtitle (optional) — header customisation
 */
export default function QAConsole({ token = null, title = "Questions", subtitle = null }) {
  const [status, setStatus] = useState("pending");
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (token) params.token = token;
      const r = await api.get("/admin/questions", { params });
      setGroups(r.data.groups || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20_000);
    return () => clearInterval(id);
  }, [load]);

  const act = async (qid, action) => {
    setBusy((p) => ({ ...p, [qid]: true }));
    try {
      const params = token ? { token } : undefined;
      await api.post(`/admin/questions/${qid}/${action}`, null, { params });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((p) => ({ ...p, [qid]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="qa-console">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#28BBE3]/10 flex items-center justify-center">
            <MessageSquare size={17} className="text-[#28BBE3]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Q&amp;A</div>
            <h2 className="font-heading text-2xl tracking-tight text-slate-900 font-medium">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full bg-white ring-1 ring-black/5 p-1 text-sm">
            {["pending", "asked", "dismissed", "all"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`px-3 sm:px-4 py-1.5 rounded-full transition capitalize ${
                  status === s ? "bg-[#28BBE3] text-white" : "text-slate-600"
                }`}
                data-testid={`qa-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-slate-300"
            onClick={load}
            disabled={loading}
            data-testid="qa-refresh-btn"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="text-xs text-slate-500">
        {total} {status} question{total === 1 ? "" : "s"} · auto-refreshes every 20s
      </div>

      {groups.length === 0 ? (
        <div className="rounded-3xl bg-white ring-1 ring-black/5 p-12 text-center text-slate-400">
          No {status === "all" ? "" : status} questions yet.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.session_id} className="rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden" data-testid={`qa-group-${g.session_id}`}>
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/60">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  {g.day_label || ""}{g.time ? ` · ${g.time}` : ""}{g.track ? ` · ${g.track}` : ""}
                </div>
                <div className="mt-1 font-heading text-lg text-slate-900 font-medium tracking-tight">{g.title}</div>
                {g.speaker && <div className="text-sm text-slate-500">{g.speaker}</div>}
              </div>
              <div className="divide-y divide-slate-100">
                {g.questions.map((q) => (
                  <div
                    key={q.question_id}
                    className="px-6 py-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-5"
                    data-testid={`qa-item-${q.question_id}`}
                  >
                    {(q.vote_count || 0) > 0 && (
                      <div className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#28BBE3]/10 text-[#28BBE3] px-2.5 py-1 text-xs font-semibold" data-testid={`qa-votes-${q.question_id}`}>
                        ▲ {q.vote_count}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 leading-relaxed">{q.text}</p>
                      <div className="mt-1.5 text-xs text-slate-500">
                        <span className="font-medium text-slate-700">{q.user_name || q.user_email}</span>
                        {q.user_name && q.user_email && <span className="text-slate-400"> · {q.user_email}</span>}
                        <span className="text-slate-400"> · {fmtTime(q.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {q.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                            disabled={!!busy[q.question_id]}
                            onClick={() => act(q.question_id, "mark-asked")}
                            data-testid={`qa-mark-asked-${q.question_id}`}
                          >
                            <CheckCircle2 size={13} className="mr-1.5" /> Mark asked
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full border-slate-300 text-slate-600"
                            disabled={!!busy[q.question_id]}
                            onClick={() => act(q.question_id, "dismiss")}
                            data-testid={`qa-dismiss-${q.question_id}`}
                          >
                            <X size={13} className="mr-1.5" /> Dismiss
                          </Button>
                        </>
                      )}
                      {q.status !== "pending" && (
                        <>
                          <span className={`text-[11px] uppercase tracking-[0.18em] font-semibold rounded-full px-3 py-1 ${
                            q.status === "asked" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                          }`}>
                            {q.status}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-full text-slate-500 hover:text-slate-900"
                            disabled={!!busy[q.question_id]}
                            onClick={() => act(q.question_id, "restore")}
                            data-testid={`qa-restore-${q.question_id}`}
                          >
                            <RotateCcw size={12} className="mr-1.5" /> Restore
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}
